/**
 * OwnerFormSheet
 * Sheet deslizable para crear o editar un dueño (role_id = 2).
 *  - Modo crear  → owner prop omitido / null
 *  - Modo editar → owner prop con el dueño existente
 *
 * El dueño tiene acceso a todas las sucursales de su empresa,
 * por lo que solo se asigna empresa (no sucursal).
 *
 * En modo editar incluye una zona de estado para dar de baja o reactivar.
 * El estilo sigue el mismo patrón que CompanyFormSheet / BranchFormSheet.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Briefcase,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Trash2,
  Link2,
  Copy,
  Check,
  Package,
  DollarSign,
  BarChart3,
  Boxes,
  Users,
  Tag,
  Pencil,
  KeyRound,
  Unlock,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { usersService, type User } from "@/services/usersService";
import { type Company } from "@/services/companiesService";
import UserAvatar from "@/components/UserAvatar";
import { verifyCredentials, getCurrentUser } from "@/services/authService";
import DeleteWithAuthDialog from "@/components/DeleteWithAuthDialog";
import { createInvitation } from "@/services/invitationsService";
import { copyToClipboard } from "@/lib/clipboard";

// ─── modules_config types & helpers ──────────────────────────────────────────

/** CRUD sub-permisos (products / stock). */
type CrudFlags = { view: boolean; create: boolean; edit: boolean; delete: boolean };

interface ModulesConfig {
  /** Prefer nested CRUD; boolean still accepted when reading legacy configs. */
  stock: CrudFlags;
  prices: boolean;
  products: CrudFlags;
  customers: boolean;
  discounts: boolean;
  "financial-summary": { view: boolean; ventas: boolean; turnos: boolean };
  [key: string]: unknown;
}

const CRUD_OFF: CrudFlags = { view: false, create: false, edit: false, delete: false };
const CRUD_ON: CrudFlags = { view: true, create: true, edit: true, delete: true };

function normalizeCrud(raw: unknown, fallback: CrudFlags = CRUD_OFF): CrudFlags {
  if (raw === true) return { ...CRUD_ON };
  if (raw === false || raw == null) return { ...fallback };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const adjust = o.adjust === true;
    return {
      view: o.view === true || adjust,
      create: o.create === true || adjust,
      edit: o.edit === true || adjust,
      delete: o.delete === true || adjust,
    };
  }
  return { ...fallback };
}

/** Defaults para dueños: todo en true, excepto turnos */
const DEFAULT_MODULES_OWNER: ModulesConfig = {
  stock: { ...CRUD_ON },
  prices: true,
  products: { view: true, create: true, edit: true, delete: false },
  customers: true,
  discounts: true,
  "financial-summary": { view: true, ventas: true, turnos: false },
};

function normalizeModulesOwner(raw?: Record<string, unknown>): ModulesConfig {
  const r = (raw ?? {}) as Record<string, any>;
  return {
    stock: normalizeCrud(r.stock, CRUD_ON),
    prices: r.prices ?? true,
    products: (() => {
      const p = normalizeCrud(r.products, CRUD_ON);
      // Product delete is not offered in the admin panel
      return { ...p, delete: false };
    })(),
    customers: r.customers ?? true,
    discounts: r.discounts ?? true,
    "financial-summary": {
      view: r["financial-summary"]?.view ?? true,
      ventas: r["financial-summary"]?.ventas ?? true,
      turnos: r["financial-summary"]?.turnos ?? false,
    },
  };
}

function crudAnyOn(c: CrudFlags): boolean {
  return c.view || c.create || c.edit || c.delete;
}

function ModuleRow({
  label,
  description,
  icon,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  description?: string;
  icon: React.ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="text-white/40 flex-shrink-0">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-white leading-tight">{label}</p>
          {description && (
            <p className="text-xs text-white/30 leading-tight mt-0.5">{description}</p>
          )}
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="flex-shrink-0 data-[state=checked]:bg-blue-600"
      />
    </div>
  );
}

function SubRow({
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-white/60">{label}</p>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="flex-shrink-0 data-[state=checked]:bg-blue-500"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface OwnerFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Dueño a editar. Omitir o pasar null para modo creación. */
  owner?: User | null;
  /** Lista de empresas disponibles para el selector. */
  companies: Company[];
  onSave: (owner?: User) => void;
}

export default function OwnerFormSheet({
  isOpen,
  onClose,
  owner,
  companies,
  onSave,
}: OwnerFormSheetProps) {
  const isEditing = !!owner;

  const [companyId, setCompanyId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [modulesConfig, setModulesConfig] = useState<ModulesConfig>(DEFAULT_MODULES_OWNER);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Estado de invitación (solo modo crear)
  const [invitationUrl, setInvitationUrl] = useState("");
  const [invitationGenerated, setInvitationGenerated] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Edit-mode unlock states
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authInput, setAuthInput] = useState("");
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(false);
  const [authError, setAuthError] = useState("");
  const [passwordUnlocked, setPasswordUnlocked] = useState(false);

  // Sync campos cuando cambia el dueño o se abre el panel
  useEffect(() => {
    if (isOpen) {
      setCompanyId(owner?.company_id ?? "");
      setFirstName(owner?.first_name ?? "");
      setLastName(owner?.last_name ?? "");
      setEmail(owner?.email ?? "");
      setPassword("");
      setModulesConfig(normalizeModulesOwner(owner?.modules_config as Record<string, unknown> | undefined));
      // Resetear estado de invitación al abrir
      setInvitationGenerated(false);
      setInvitationUrl("");
      setIsCopied(false);
      // Resetear estados de edición protegida
      setIsEditingInfo(false);
      setShowAuthPrompt(false);
      setAuthInput("");
      setAuthError("");
      setPasswordUnlocked(false);
    }
  }, [isOpen, owner]);

  const handleClose = () => {
    if (isSaving || isToggling) return;
    onClose();
  };

  const handleSave = async () => {
    if (isEditing && (!firstName.trim() || !lastName.trim())) {
      toast.error("Nombre y apellido son obligatorios");
      return;
    }
    if (!companyId) {
      toast.error("Debes seleccionar una empresa");
      return;
    }

    setIsSaving(true);
    try {
      let saved: User;
      if (isEditing) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updateData: any = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim() || null,
          company_id: companyId,
          branch_id: null, // dueños no tienen sucursal específica
          modules_config: modulesConfig,
        };
        if (password) updateData.password = password;
        saved = await usersService.updateUser(owner.id, updateData);
        toast.success("Dueño actualizado correctamente");
      } else {
        // Modo invitación: generar URL en lugar de crear directamente
        const inv = await createInvitation({
          company_id: companyId,
          role_id: 2,
          modules_config: modulesConfig,
        });
        setInvitationUrl(inv.invitation_url);
        setInvitationGenerated(true);
        toast.success("Invitación generada correctamente");
        return; // NO cerrar el panel — mostrar la URL
      }
      onSave(saved);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar el dueño"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!owner) return;
    setIsToggling(true);
    try {
      let updated: User;
      if (owner.is_active) {
        updated = await usersService.deactivateUser(owner.id);
        toast.success("Dueño dado de baja correctamente");
      } else {
        updated = await usersService.activateUser(owner.id);
        toast.success("Dueño reactivado correctamente");
      }
      onSave(updated);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cambiar el estado"
      );
    } finally {
      setIsToggling(false);
    }
  };

  const handleDeleteConfirm = async (password: string) => {
    if (!owner) return;
    const currentUser = getCurrentUser();
    if (!currentUser) { toast.error("Sesión no encontrada"); return; }
    setIsDeleting(true);
    try {
      const valid = await verifyCredentials(currentUser.username, password);
      if (!valid) { toast.error("Contraseña incorrecta"); return; }
      await usersService.deleteUser(owner.id);
      toast.success("Dueño eliminado correctamente");
      setIsDeleteOpen(false);
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al eliminar el dueño");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelInfoEdit = () => {
    setFirstName(owner?.first_name ?? "");
    setLastName(owner?.last_name ?? "");
    setEmail(owner?.email ?? "");
    setCompanyId(owner?.company_id ?? "");
    setIsEditingInfo(false);
  };

  const handleVerifyAuth = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) { toast.error("Sesión no encontrada"); return; }
    setIsVerifyingAuth(true);
    setAuthError("");
    try {
      const valid = await verifyCredentials(currentUser.username, authInput);
      if (valid) {
        setPasswordUnlocked(true);
        setShowAuthPrompt(false);
        setAuthInput("");
      } else {
        setAuthError("Contraseña incorrecta");
      }
    } catch {
      setAuthError("Error al verificar. Intenta de nuevo.");
    } finally {
      setIsVerifyingAuth(false);
    }
  };

  const initials = isEditing ? usersService.getInitials(owner) : "?";

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            {isEditing ? (
              <UserAvatar initials={initials} role="owner" size="xl" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white flex-shrink-0">
                <Briefcase className="h-7 w-7" />
              </div>
            )}
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {isEditing
                  ? usersService.getFullName(owner)
                  : "Nuevo dueño"}
              </SheetTitle>
              {isEditing && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-sm text-white/50">@{owner.username}</span>
                  <Badge
                    className={
                      owner.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {owner.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Cuerpo con scroll */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Datos de acceso — solo en edición */}
            {isEditing && (
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Datos de acceso
              </p>

              {/* Usuario — siempre read-only */}
              <div className="space-y-1.5">
                <p className="text-xs text-white/30">Usuario</p>
                <p className="text-sm text-white font-mono">@{owner.username}</p>
              </div>

              {/* Contraseña — protegida por autenticación */}
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Contraseña</Label>
                {!passwordUnlocked ? (
                  <div className="space-y-3">
                    {!showAuthPrompt ? (
                      <button
                        type="button"
                        onClick={() => setShowAuthPrompt(true)}
                        disabled={isSaving || isToggling}
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                      >
                        <KeyRound className="h-4 w-4" />
                        Cambiar contraseña
                      </button>
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
                        <p className="text-xs text-white/50">
                          Confirma tu contraseña de administrador para continuar
                        </p>
                        <Input
                          type="password"
                          value={authInput}
                          onChange={(e) => { setAuthInput(e.target.value); setAuthError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && authInput.trim() && handleVerifyAuth()}
                          placeholder="Tu contraseña actual"
                          autoComplete="current-password"
                          autoFocus
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                     focus:bg-white/10 focus:border-white/40 h-9 text-sm"
                        />
                        {authError && (
                          <p className="text-xs text-red-400">{authError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleVerifyAuth}
                            disabled={isVerifyingAuth || !authInput.trim()}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs px-3"
                          >
                            {isVerifyingAuth
                              ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                              : <ShieldCheck className="h-3 w-3 mr-1.5" />}
                            Verificar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setShowAuthPrompt(false); setAuthInput(""); setAuthError(""); }}
                            disabled={isVerifyingAuth}
                            className="text-white/50 hover:text-white hover:bg-white/10 h-7 text-xs px-3"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Input
                      id="owner-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Nueva contraseña"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                 focus:bg-white/10 focus:border-white/40"
                      disabled={isSaving || isToggling}
                      autoComplete="new-password"
                      autoFocus
                    />
                    <p className="text-xs text-emerald-400/80 flex items-center gap-1">
                      <Unlock className="h-3 w-3" />
                      Campo habilitado — deja vacío para no cambiar
                    </p>
                  </div>
                )}
              </div>
            </section>
            )}

            {isEditing && <Separator className="bg-white/10" />}

            {/* Información personal — solo en edición */}
            {isEditing && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  Información personal
                </p>
                {!isEditingInfo ? (
                  <button
                    type="button"
                    onClick={() => setIsEditingInfo(true)}
                    disabled={isSaving || isToggling}
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                  >
                    <Pencil className="h-3 w-3" />
                    Editar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={cancelInfoEdit}
                    disabled={isSaving}
                    className="text-xs text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>

              {!isEditingInfo ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-white/30 mb-0.5">Nombre completo</p>
                    <p className="text-sm text-white">{firstName} {lastName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/30 mb-0.5">Correo electrónico</p>
                    {email
                      ? <p className="text-sm text-white">{email}</p>
                      : <p className="text-sm text-white/30">Sin correo registrado</p>}
                  </div>
                </div>
              ) : (
                <>
                  {/* Nombre + Apellido en grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="owner-firstname" className="text-white/70 text-sm">
                        Nombre <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="owner-firstname"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Juan"
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                   focus:bg-white/10 focus:border-white/40"
                        disabled={isSaving || isToggling}
                        autoFocus
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="owner-lastname" className="text-white/70 text-sm">
                        Apellido <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="owner-lastname"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Pérez"
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                   focus:bg-white/10 focus:border-white/40"
                        disabled={isSaving || isToggling}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="owner-email" className="text-white/70 text-sm">
                      Correo electrónico{" "}
                      <span className="text-white/30 text-xs">(opcional)</span>
                    </Label>
                    <Input
                      id="owner-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="juan@empresa.com"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                 focus:bg-white/10 focus:border-white/40"
                      disabled={isSaving || isToggling}
                      autoComplete="off"
                    />
                  </div>
                </>
              )}
            </section>
            )}

            <Separator className="bg-white/10" />

            {/* Empresa */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  Asignación
                </p>
                {isEditing && !isEditingInfo && (
                  <button
                    type="button"
                    onClick={() => setIsEditingInfo(true)}
                    disabled={isSaving || isToggling}
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                  >
                    <Pencil className="h-3 w-3" />
                    Editar
                  </button>
                )}
                {isEditing && isEditingInfo && (
                  <button
                    type="button"
                    onClick={cancelInfoEdit}
                    disabled={isSaving}
                    className="text-xs text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>

              {isEditing && !isEditingInfo ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-white/30 mb-0.5">Empresa</p>
                  <p className="text-sm text-white">
                    {companies.find((c) => c.id === companyId)?.name || "—"}
                  </p>
                  <p className="text-xs text-white/30 mt-1">
                    El dueño tiene acceso a todas las sucursales de esta empresa.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="owner-company" className="text-white/70 text-sm">
                    Empresa <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={companyId}
                    onValueChange={setCompanyId}
                    disabled={isSaving || isToggling}
                  >
                    <SelectTrigger
                      id="owner-company"
                      className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40"
                    >
                      <SelectValue placeholder="Seleccionar empresa..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/10 text-white">
                      {companies
                        .filter((c) => c.is_active)
                        .map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            className="focus:bg-white/10 focus:text-white"
                          >
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-white/30">
                    El dueño tendrá acceso a todas las sucursales de la empresa seleccionada.
                  </p>
                </div>
              )}
            </section>

            {/* Permisos del panel */}
            <>
              <Separator className="bg-white/10" />
              <section className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  <ShieldCheck className="inline-block h-3 w-3 mr-1 mb-0.5" />
                  Acceso al panel de administración
                </p>
                <p className="text-xs text-white/30">
                  Los dueños tienen acceso completo por defecto. Puedes ajustar los permisos a continuación.
                </p>

                {/* Inventario / Stock */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Inventario / Stock"
                    description="Ver y ajustar niveles de inventario"
                    icon={<Boxes className="h-4 w-4" />}
                    checked={crudAnyOn(modulesConfig.stock)}
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        stock: { view: v, create: v, edit: v, delete: v },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver listado"
                      checked={modulesConfig.stock.view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, view: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Agregar"
                      checked={modulesConfig.stock.create}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, create: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Editar"
                      checked={modulesConfig.stock.edit}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, edit: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Reducir"
                      checked={modulesConfig.stock.delete}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, delete: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>

                {/* Precios */}
                <ModuleRow
                  label="Precios"
                  description="Ver y gestionar precios de productos"
                  icon={<DollarSign className="h-4 w-4" />}
                  checked={modulesConfig.prices}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, prices: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Productos */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Productos"
                    description="Módulo de gestión de productos"
                    icon={<Package className="h-4 w-4" />}
                    checked={crudAnyOn(modulesConfig.products)}
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        products: { view: v, create: v, edit: v, delete: false },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver listado"
                      checked={modulesConfig.products.view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, view: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Crear"
                      checked={modulesConfig.products.create}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, create: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Editar"
                      checked={modulesConfig.products.edit}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, edit: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>

                {/* Clientes */}
                <ModuleRow
                  label="Clientes"
                  description="Gestión de clientes y cuentas corrientes"
                  icon={<Users className="h-4 w-4" />}
                  checked={modulesConfig.customers}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, customers: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Descuentos */}
                <ModuleRow
                  label="Descuentos"
                  description="Configuración y aplicación de descuentos"
                  icon={<Tag className="h-4 w-4" />}
                  checked={modulesConfig.discounts}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, discounts: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Resumen financiero */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Resumen financiero"
                    description="Dashboard de ventas y turnos"
                    icon={<BarChart3 className="h-4 w-4" />}
                    checked={
                      modulesConfig["financial-summary"].view ||
                      modulesConfig["financial-summary"].ventas ||
                      modulesConfig["financial-summary"].turnos
                    }
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        "financial-summary": { view: v, ventas: v, turnos: v },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver resumen"
                      checked={modulesConfig["financial-summary"].view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": { ...p["financial-summary"], view: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Ventas"
                      checked={modulesConfig["financial-summary"].ventas}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": { ...p["financial-summary"], ventas: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Turnos"
                      checked={modulesConfig["financial-summary"].turnos}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": { ...p["financial-summary"], turnos: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>
              </section>
            </>

            {/* Invitación generada */}
            {!isEditing && invitationGenerated && (
              <>
                <Separator className="bg-white/10" />
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/80">
                    <Link2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                    Invitación lista
                  </p>
                  <p className="text-xs text-white/50">
                    Comparte este enlace con el futuro dueño. Expira en 7 días.
                  </p>
                  <div className="flex items-center gap-2 bg-white/5 rounded-lg border border-white/10 px-3 py-2">
                    <p className="text-xs text-blue-300 break-all flex-1 select-all">
                      {invitationUrl}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        copyToClipboard(invitationUrl);
                        setIsCopied(true);
                        setTimeout(() => setIsCopied(false), 2000);
                      }}
                      className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 transition-colors"
                      title="Copiar enlace"
                    >
                      {isCopied
                        ? <Check className="h-4 w-4 text-emerald-400" />
                        : <Copy className="h-4 w-4 text-white/50" />}
                    </button>
                  </div>
                </section>
              </>
            )}

            {/* Zona de estado — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10" />

                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Estado del usuario
                  </p>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      {owner.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      )}
                      <p className="text-sm text-white/70">
                        {owner.is_active
                          ? "El dueño está activo y puede iniciar sesión."
                          : "El dueño está dado de baja y no puede acceder al sistema."}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleActive}
                      disabled={isToggling || isSaving}
                      className={
                        owner.is_active
                          ? "w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500/60 bg-transparent"
                          : "w-full border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/60 bg-transparent"
                      }
                    >
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : owner.is_active ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      {owner.is_active ? "Dar de baja" : "Reactivar dueño"}
                    </Button>
                  </div>
                </section>

                <Separator className="bg-white/10" />

                {/* Zona de peligro */}
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-red-400/70">
                    Zona de peligro
                  </p>
                  <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-3">
                    <p className="text-xs text-white/40">
                      Elimina al dueño permanentemente del sistema. Si solo quieres
                      bloquear el acceso, usa &ldquo;Dar de baja&rdquo; más arriba.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsDeleteOpen(true)}
                      disabled={isSaving || isToggling || isDeleting}
                      className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar dueño permanentemente
                    </Button>
                  </div>
                </section>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer fijo */}
        <SheetFooter
          className="flex-shrink-0 flex flex-row justify-center sm:justify-end gap-2 px-6 py-4
                     border-t border-white/10 bg-slate-900"
        >
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isToggling}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          {isEditing ? (
            <Button
              onClick={handleSave}
              disabled={isSaving || isToggling}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          ) : invitationGenerated ? (
            <Button
              onClick={() => {
                setInvitationGenerated(false);
                setInvitationUrl("");
                setCompanyId("");
                setModulesConfig(DEFAULT_MODULES_OWNER);
              }}
              variant="outline"
              className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
            >
              <Link2 className="h-4 w-4 mr-2" />
              Nueva invitación
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Generar invitación
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {isEditing && owner && (
      <DeleteWithAuthDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        resourceType="Dueño"
        resourceName={usersService.getFullName(owner)}
        cascadeItems={[]}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    )}
    </>
  );
}
